import clsx from 'clsx';
import _ from 'lodash';
import React from 'react';
import { useIntl } from 'react-intl';
import {
  columnUserLabelMapper,
  dangerBooleanColumnMapper,
  durationColumnMapper,
} from 'src/utils/label-mapper';
import Typography from '@material-ui/core/Typography';
import Lock from '@material-ui/icons/Lock';
import {
  ApiFilterOperation,
  FieldFilter,
  FilterComponentProps,
  Select,
  TableCells,
  TableColumn,
  TableFilterCells,
  TextField,
  TimeField,
  useCompareOperations,
  useFilterOperation,
  useFilterStyles,
  useStaticListSource,
} from '@eas/common-web';
import { useCertifications } from '@modules/certification/certification-api';
import { useDepartmentAutocomplete } from '@modules/department/department-api';
import { useMobileUnits } from '@modules/mobile-unit/mobile-unit-api';
import { useSectionAutocomplete } from '@modules/section/section-api';
import { useTeamAutocomplete } from '@modules/team/team-api';
import { useTechnicalObjectTypes } from '@modules/technical-object/technical-object-columns';
import { useUsersAutocomplete } from '@modules/user/user-api';
import { useWorkOrderStates } from '@modules/work-order-state/work-order-state-api';
import { useWorkOrderTypes } from '@modules/work-order-type/work-order-type-api';
import { WorkOrder } from '@models';
import { universalUserLabelMapper } from '@utils';
import { useWorkOrders } from './work-order-api';
import { useWorkOrderStateColumn } from './work-order-utils';

export function useStringOperations() {
  return useStaticListSource<{ id: ApiFilterOperation; name: string }>([
    {
      id: ApiFilterOperation.CONTAINS,
      name: 'Obsahuje',
    },
    {
      id: ApiFilterOperation.EQ,
      name: '=',
    },
  ]);
}

function CurrentStateCell({ rowValue }: { rowValue: WorkOrder }) {
  return useWorkOrderStateColumn(rowValue.currentState);
}
CurrentStateCell.displayName = 'TextCell';

function DurationFilterComponent({
  disabled,
  value,
  onChange,
  state,
  onChangeState,
}: FilterComponentProps) {
  const classes = useFilterStyles();
  const operations = useCompareOperations();
  const { handleChangeOperation } = useFilterOperation(state, onChangeState);

  // seconds to HH:MM
  const hours = Math.floor(value / 3600)
    .toString()
    .padStart(2, '0');
  const minutes = Math.floor((value % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = (value % 60).toString().padStart(2, '0');
  const filterValue = `${hours}:${minutes}:${seconds}`;

  const handleChange = (newValue: string | null) => {
    if (newValue === null) {
      onChange(null);
    } else {
      const [hours, minutes, seconds] = newValue.split(':');

      onChange(
        parseInt(hours) * 3600 + parseInt(minutes) * 60 + parseInt(seconds)
      );
    }
  };

  return (
    <div className={clsx(classes.root, { [classes.disabled]: disabled })}>
      <div className={classes.selectWrapper}>
        <Select
          source={operations}
          value={state.operation}
          onChange={handleChangeOperation}
          valueIsId={true}
          clearable={false}
          disabled={disabled}
        />
      </div>
      <div className={classes.textWrapper}>
        <TimeField
          value={filterValue}
          onChange={handleChange}
          disabled={disabled}
          showSeconds={true}
        />
      </div>
    </div>
  );
}

function LockCell({ value }: { value?: boolean }) {
  return value ? <Lock fontSize="small" /> : <>-</>;
}
LockCell.displayName = 'BooleanCell';
/*
function TechnicalObjectNameFilterComponent(props: FilterComponentProps) {
  const { disabled, value, onChange, state, onChangeState } = props;
  console.log(props);
  const classes = useFilterStyles();

  const handleChange = (newValue: string | null) => {
    if (newValue === null) {
      onChange(null);
    } else {
      onChange(newValue);
      onChangeState({
        ...state,
        value: newValue,
        operation: ApiFilterOperation.AND,
        field: 'technicalObjects',
        filters: [
          {
            operation: ApiFilterOperation.EQ,
            //field: 'technicalObjectsSize',
            field: 'technicalObjects.size', //BE TODO:
            value: 1,
          },
          {
            operation: ApiFilterOperation.NESTED,
            path: 'technicalObjects',
            filter: {
              operation: ApiFilterOperation.CONTAINS,
              field: 'technicalObjects.name',
              value: newValue, // TODO: BE case insensitive?
              path: 'technicalObjects',
            },
          },
        ],
      });
    }
  };

  return (
    <div className={clsx(classes.root, { [classes.disabled]: disabled })}>
      <div className={classes.textWrapper}>
        <TextField value={value} onChange={handleChange} disabled={disabled} />
      </div>
    </div>
  );
}

function TechnicalObjectTypeFilterComponent(props: FilterComponentProps) {
  const { disabled, value, onChange, state, onChangeState } = props;
  console.log(props);
  const classes = useFilterStyles();

  const handleChange = (
    newValue: string | DomainObject | DomainObject[] | null
  ) => {
    console.log({ newValue });
    if (newValue === null) {
      onChange(null);
    } else {
      onChange(newValue);
      onChangeState({
        ...state,
        value: newValue,
        operation: ApiFilterOperation.AND,
        field: 'technicalObjects',
        filters: [
          {
            operation: ApiFilterOperation.NESTED,
            path: 'technicalObjects',
            filter: {
              operation: ApiFilterOperation.IN,
              field: 'technicalObjects.type.id',
              values: Array.isArray(newValue)
                ? newValue.map((t) => t?.id)
                : [newValue],
              path: 'technicalObjects',
            },
          },
        ],
      });
    }
  };

  return (
    <div className={clsx(classes.root, { [classes.disabled]: disabled })}>
      <div className={classes.textWrapper}>
        <Autocomplete
          source={useTechnicalObjectTypes()}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          multiple
        />
      </div>
    </div>
  );
}
*/
function getTechnicalObjectCell(
  nameKey: string,
  getPostfix: (length: number) => string
) {
  function TextCell({ rowValue }: { rowValue: WorkOrder }) {
    if ((rowValue.technicalObjects?.length ?? 0) === 0) {
      return null;
    }
    const tos = rowValue.technicalObjects ?? [];
    const value =
      tos.length > 2 ? (
        <>
          {_.get(tos[0], nameKey)}, <i>{getPostfix(tos.length - 1)}</i>
        </>
      ) : (
        <>{tos.map((t) => _.get(t, nameKey)).join(', ')}</>
      );

    return (
      <Typography
        variant="body1"
        style={{
          padding: '0 8px 0 4px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {value}
      </Typography>
    );
  }
  TextCell.displayName = 'TextCell';
  return TextCell;
}

function WorkorderIdFilterComponent({
  disabled,
  onChange,
  state,
  onChangeState,
  value,
}: FilterComponentProps) {
  const classes = useFilterStyles();

  const handleChange = (newValue: string | null) => {
    const workOrderIds = (newValue ?? '')
      .split(' ')
      .map((id) => id.trim())
      .filter((s) => s);

    if (newValue === null || workOrderIds.length === 0) {
      onChange(null);
      onChangeState({
        ...state,
        value: null,
        filters: [],
      });
    } else {
      onChangeState({
        ...state,
        value: newValue,
        operation: ApiFilterOperation.AND,
        filters: [
          {
            operation: ApiFilterOperation.IN,
            field: 'workOrderId',
            values: workOrderIds,
          },
        ],
      });
    }
  };

  return (
    <div className={clsx(classes.root, { [classes.disabled]: disabled })}>
      <div className={classes.textWrapper}>
        <TextField
          value={value ?? ''}
          onChange={handleChange}
          multiline
          inputProps={{ rowsMax: 3, maxLength: 10000 }}
          disabled={disabled}
          onClear={() =>
            onChangeState({
              ...state,
              value: null,
              operation: ApiFilterOperation.AND,
              filters: [],
            })
          }
        />
      </div>
    </div>
  );
}

export function useColumns(): TableColumn<WorkOrder>[] {
  // temp argument - https://jira.zsdis.sk/browse/FSMS-1283
  // newAddressFieldsSortAndFilter?: boolean

  const intl = useIntl();

  return [
    {
      datakey: 'workOrderId',
      sortkey: 'workOrderId',
      filterkey: 'workOrderId',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CONTRACT_NUMBER',
        defaultMessage: 'Číslo zákazky',
      }),
      width: 150,
      CellComponent: TableCells.NumberCell,
      sortable: true,
      filterable: true,
      FilterComponent: WorkorderIdFilterComponent,
      filterOperation: ApiFilterOperation.AND,
      filterGroup: 0,
      filterOrder: 0,
    },
    {
      datakey: 'operationNumber',
      sortkey: 'operationNumber',
      filterkey: 'operationNumber',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__OPERATION_NUMBER',
        defaultMessage: 'Číslo operácie',
      }),
      width: 150,
      CellComponent: TableCells.NumberCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 0,
      filterOrder: 1,
    },
    {
      datakey: 'received',
      sortkey: 'received',
      filterkey: 'received',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__RECEIVED',
        defaultMessage: 'PP prijatý na MK',
      }),
      width: 80,
      CellComponent: TableCells.BooleanCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterBooleanCell,
      filterGroup: 0,
      filterOrder: 2,
      visible: false,
    },
    {
      datakey: 'priority',
      sortkey: 'priority',
      filterkey: 'priority',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__PRIORITY',
        defaultMessage: 'Priorita',
      }),
      width: 80,
      CellComponent: TableCells.NumberCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 0,
      filterOrder: 3,
    },
    {
      datakey: 'currentState',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__STATE',
        defaultMessage: 'Stav',
      }),
      width: 100,
      CellComponent: CurrentStateCell,
      sortable: true,
      sortkey: 'currentState.name',
      filterable: true,
      filterkey: 'currentState.id',
      filterOperation: ApiFilterOperation.EQ,
      FilterComponent:
        TableFilterCells.useFilterMultipleAutocompleteCellFactory(
          useWorkOrderStates,
          (state) => state.label ?? ''
        ),
      filterGroup: 0,
      filterOrder: 3,
      valueMapper: TableCells.dictionaryColumnMapper,
    },
    {
      datakey: 'workOrderType',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__WORK_ORDER_Type',
        defaultMessage: 'Druh PP',
      }),
      width: 100,
      sortable: true,
      sortkey: 'workOrderType.name',
      filterable: true,
      filterkey: 'workOrderType.id',
      filterOperation: ApiFilterOperation.EQ,
      FilterComponent:
        TableFilterCells.useFilterMultipleAutocompleteCellFactory(
          useWorkOrderTypes
        ),
      filterGroup: 0,
      filterOrder: 4,
      valueMapper: TableCells.dictionaryColumnMapper,
      CellComponent: TableCells.TextCell,
    },
    {
      datakey: 'department.name',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__DEPARTMENT',
        defaultMessage: 'Pracovisko',
      }),
      width: 150,
      sortable: true,
      sortkey: 'department.name',
      filterable: true,
      filterkey: 'department.id',
      FilterComponent:
        TableFilterCells.useFilterMultipleAutocompleteCellFactory(
          useDepartmentAutocomplete
        ),
      filterGroup: 1,
      filterOrder: 0,
      // valueMapper: TableCells.dictionaryColumnMapper,
      CellComponent: TableCells.TextCell,
    },
    {
      datakey: 'department.team.name',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__TEAM',
        defaultMessage: 'Tím',
      }),
      width: 150,
      sortable: true,
      sortkey: 'department.team.name',
      filterable: true,
      filterkey: 'department.team.id',
      FilterComponent:
        TableFilterCells.useFilterMultipleAutocompleteCellFactory(
          useTeamAutocomplete
        ),
      filterGroup: 1,
      filterOrder: 1,
      // valueMapper: TableCells.dictionaryColumnMapper,
      CellComponent: TableCells.TextCell,
    },
    {
      datakey: 'department.team.section.name',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__SECTION',
        defaultMessage: 'Úsek',
      }),
      width: 150,
      sortable: true,
      sortkey: 'department.team.section.name',
      filterable: true,
      filterkey: 'department.team.section.id',
      FilterComponent:
        TableFilterCells.useFilterMultipleAutocompleteCellFactory(
          useSectionAutocomplete
        ),
      filterGroup: 1,
      filterOrder: 2,
      // valueMapper: TableCells.dictionaryColumnMapper,
      CellComponent: TableCells.TextCell,
    },
    {
      datakey: 'mobileUnit',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__MOBILE_UNIT',
        defaultMessage: 'Priradený k mobilnej jednotke',
      }),
      width: 150,
      sortable: true,
      sortkey: 'mobileUnit.name',
      filterable: true,
      filterkey: 'mobileUnit.id',
      FilterComponent:
        TableFilterCells.useFilterMultipleAutocompleteCellFactory(
          useMobileUnits,
          (mu) => mu.label ?? ''
        ),
      filterGroup: 1,
      filterOrder: 3,
      valueMapper: TableCells.dictionaryColumnMapper,
      CellComponent: TableCells.TextCell,
    },
    {
      datakey: 'user',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__USER',
        defaultMessage: 'Priradený k technikovi',
      }),
      width: 150,
      sortable: true,
      sortkey: 'user.firstName',
      filterable: true,
      filterkey: 'user.id',
      FilterComponent:
        TableFilterCells.useFilterMultipleAutocompleteCellFactory(
          useUsersAutocomplete,
          universalUserLabelMapper
        ),
      filterGroup: 1,
      filterOrder: 4,
      valueMapper: columnUserLabelMapper,
      CellComponent: TableCells.TextCell,
    },
    {
      datakey: 'locked',
      sortkey: 'locked',
      filterkey: 'locked',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__LOCKED',
        defaultMessage: 'Uzamknutý',
      }),
      width: 80,
      CellComponent: LockCell,
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterBooleanCell,
      filterGroup: 1,
      filterOrder: 5,
      visible: false,
    },
    {
      datakey: 'fixed',
      sortkey: 'fixed',
      filterkey: 'fixed',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__FIXED',
        defaultMessage: 'Fixný',
      }),
      width: 80,
      CellComponent: TableCells.BooleanCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterBooleanCell,
      filterGroup: 1,
      filterOrder: 6,
      visible: false,
    },
    {
      datakey: 'indivisible',
      sortkey: 'indivisible',
      filterkey: 'indivisible',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__INDIVISIBLE',
        defaultMessage: 'Neprerušitelná práca',
      }),
      width: 80,
      CellComponent: TableCells.BooleanCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterBooleanCell,
      filterGroup: 1,
      filterOrder: 7,
      visible: false,
    },
    {
      datakey: 'customerAgreed',
      sortkey: 'customerAgreed',
      filterkey: 'customerAgreed',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CUSTOMER_AGREED',
        defaultMessage: 'Dohodnuté so zákazníkom',
      }),
      width: 80,
      CellComponent: TableCells.BooleanCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterBooleanCell,
      filterGroup: 1,
      filterOrder: 8,
      visible: false,
    },
    {
      datakey: 'maintenanceFailure',
      sortkey: 'maintenanceFailure',
      filterkey: 'maintenanceFailure',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__MAINTENANCE_FAILURE',
        defaultMessage: 'Porucha v údržbe (PVU)',
      }),
      width: 80,
      CellComponent: TableCells.BooleanCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterBooleanCell,
      filterGroup: 1,
      filterOrder: 9,
      visible: false,
    },
    {
      datakey: 'inDanger',
      sortkey: 'inDanger',
      filterkey: 'inDanger',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__IN_DANGER',
        defaultMessage: 'Nebezpečenstvo',
      }),
      width: 80,
      CellComponent: function TextCell({ value, ...props }) {
        if (value === 1) {
          return <TableCells.TextCell value="Nie" {...props} />;
        } else if (value === 2) {
          return (
            <div
              style={{
                backgroundColor: 'red',
                color: 'white',
                padding: '0 8px',
              }}
            >
              <TableCells.TextCell value="Áno" {...props} />
            </div>
          );
        }
        return <TableCells.TextCell value="" {...props} />;
      },
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 1,
      filterOrder: 10,
      valueMapper: dangerBooleanColumnMapper,
      visible: false,
    },
    {
      datakey: 'risk',
      sortkey: 'risk',
      filterkey: 'risk',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__RISK',
        defaultMessage: 'Rizikový OP',
      }),
      width: 80,
      CellComponent: TableCells.BooleanCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterBooleanCell,
      filterGroup: 1,
      filterOrder: 11,
      visible: false,
    },
    {
      datakey: 'technicalPlaceSap',
      sortkey: 'technicalPlaceSap',
      filterkey: 'technicalPlaceSap',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__TECHNICAL_PLACE_SAP',
        defaultMessage: 'Technické miesto SAP',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 12,
      visible: false,
    },
    {
      datakey: 'standardTextKey',
      sortkey: 'standardTextKey',
      filterkey: 'standardTextKey',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__STANDARD_TEXT_KEY',
        defaultMessage: 'Kľúč štandardného textu',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 14,
      visible: false,
    },
    {
      datakey: 'customerPhoneNumber',
      sortkey: 'customerPhoneNumber',
      filterkey: 'customerPhoneNumber',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CUSTOMER_PHONE_NUMBER',
        defaultMessage: 'Telefónne číslo zákazníka',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 15,
      visible: false,
    },
    {
      datakey: 'callId',
      sortkey: 'callId',
      filterkey: 'callId',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CALL_ID',
        defaultMessage: 'ID úlohy',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 16,
      visible: false,
    },
    {
      datakey: 'equipmentId',
      sortkey: 'equipmentId',
      filterkey: 'equipmentId',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__EQUIPMENT_ID',
        defaultMessage: 'Popis vybavení - id',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 17,
      visible: false,
    },
    {
      datakey: 'equipmentText',
      sortkey: 'equipmentText',
      filterkey: 'equipmentText',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__EQUIPMENT_TEXT',
        defaultMessage: 'Popis vybavení - text',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 18,
      visible: false,
    },
    {
      // TODO:
      // keep this old column to avoid saved (named) filters inconsistency
      // remove after named filters are reworked as custom name filters
      // https://jira.zsdis.sk/browse/FSMS-920
      datakey: 'customerName',
      sortkey: 'customerName',
      filterkey: 'customerName',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CUSTOMER_NAME',
        defaultMessage: '(nepoužívať) Meno zákazníka',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: false,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      visible: false,
      filterGroup: 1,
      filterOrder: 19,
    },
    {
      datakey: 'consumptionPlaceNumber',
      sortkey: 'consumptionPlaceNumber',
      filterkey: 'consumptionPlaceNumber',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CONSUMPTION_PLACE_NUMBER',
        defaultMessage: 'Číslo miesta spotreby',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 20,
      visible: false,
    },
    {
      datakey: 'hdoCode',
      sortkey: 'hdoCode',
      filterkey: 'hdoCode',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__HDO_CODE',
        defaultMessage: 'Kód HDO',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 21,
      visible: false,
    },
    {
      datakey: 'mainCircuitBreaker',
      sortkey: 'mainCircuitBreaker',
      filterkey: 'mainCircuitBreaker',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__MAIN_CIRCUIT_BRAKER',
        defaultMessage: 'Hlavný istič',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 22,
      visible: false,
    },
    {
      datakey: 'oldTariffType',
      sortkey: 'oldTariffType',
      filterkey: 'oldTariffType',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__OLD_TARIFF_TYPE',
        defaultMessage: 'Platný typ tarifu',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 23,
      visible: false,
    },
    {
      datakey: 'newTariffType',
      sortkey: 'newTariffType',
      filterkey: 'newTariffType',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__NEW_TARIFF_TYPE',
        defaultMessage: 'Požadovaný typ tarifu',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 24,
      visible: false,
    },
    {
      datakey: 'oldTariffication',
      sortkey: 'oldTariffication',
      filterkey: 'oldTariffication',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__OLD_TARIFFICATION',
        defaultMessage: 'Platná tarifikácia',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 25,
    },
    {
      datakey: 'newTarification',
      sortkey: 'newTarification',
      filterkey: 'newTarification',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__NEW_TARIFFICATION',
        defaultMessage: 'Požadovaná tarifikácia',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 26,
      visible: false,
    },
    {
      datakey: 'consumptionPointTypeDescription',
      sortkey: 'consumptionPointTypeDescription',
      filterkey: 'consumptionPointTypeDescription',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CONSUMPTION_POINT_TYPE_DESCRIPTION',
        defaultMessage: 'Popis druhu miesta spotreby',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 27,
      visible: false,
    },
    {
      datakey: 'meterSerialId',
      sortkey: 'meterSerialId',
      filterkey: 'meterSerialId',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__METER_SERIAL_ID',
        defaultMessage: 'Sériové číslo ELM',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 28,
      visible: false,
    },
    {
      datakey: 'elmMaterialType',
      sortkey: 'elmMaterialType',
      filterkey: 'elmMaterialType',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__ELM_MATERIAL_TYPE',
        defaultMessage: 'Technický popis ELM',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 29,
      visible: false,
    },
    {
      datakey: 'integratedHdoCode',
      sortkey: 'integratedHdoCode',
      filterkey: 'integratedHdoCode',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__INTEGRATED_HDO_CODE',
        defaultMessage: 'Integrovaný kód HDO',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 30,
      visible: false,
    },
    {
      datakey: 'billingArea',
      sortkey: 'billingArea',
      filterkey: 'billingArea',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__BILLING_AREA',
        defaultMessage: 'Fakturačná oblasť',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 31,
      visible: false,
    },
    {
      datakey: 'vgk1',
      sortkey: 'vgk1',
      filterkey: 'vgk1',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__VGK1',
        defaultMessage: 'VKG, 1 pole - Označenie miesta prístroja',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 32,
      visible: false,
    },
    {
      datakey: 'vgk2',
      sortkey: 'vgk2',
      filterkey: 'vgk2',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__VGK2',
        defaultMessage: 'VKG, 2 pole - Dodatok k stanovisku',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 33,
      visible: false,
    },
    {
      datakey: 'vgk3',
      sortkey: 'vgk3',
      filterkey: 'vgk3',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__VGK3',
        defaultMessage: 'VKG, 3 pole - Dlhý text k tech. miestu',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 34,
      visible: false,
    },
    {
      datakey: 'customerVulnerable',
      sortkey: 'customerVulnerable',
      filterkey: 'customerVulnerable',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CUSTOMER_VULNERABLE',
        defaultMessage: 'Zraniteľný zákazník',
      }),
      width: 150,
      CellComponent: TableCells.NumberCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 1,
      filterOrder: 35,
      visible: false,
    },
    {
      datakey: 'customerVip',
      sortkey: 'customerVip',
      filterkey: 'customerVip',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CUSTOMER_VIP',
        defaultMessage: 'VIP zákazník',
      }),
      width: 150,
      CellComponent: TableCells.NumberCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 1,
      filterOrder: 36,
      visible: false,
    },
    {
      datakey: 'ownerTo',
      sortkey: 'ownerTo',
      filterkey: 'equipownerTomentText',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__OWNER_TO',
        defaultMessage: 'Vlastník TO',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 1,
      filterOrder: 37,
      visible: false,
    },
    {
      datakey: 'numberOfTaskFailure',
      sortkey: 'numberOfTaskFailure',
      filterkey: 'numberOfTaskFailure',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__NUMBER_OF_TASK_FAILURE',
        defaultMessage: 'Počet porúch na danom TM/ OM za posledné 3 mesiace',
      }),
      width: 150,
      CellComponent: TableCells.NumberCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 1,
      filterOrder: 38,
      visible: false,
    },
    {
      datakey: 'startTime',
      sortkey: 'startTime',
      filterkey: 'startTime',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__START_TIME',
        defaultMessage: 'Termín realizácie od',
      }),
      width: 100,
      CellComponent: TableCells.DateTimeCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterLocalDateTimeCell,
      filterGroup: 2,
      filterOrder: 0,
      visible: false,
    },
    {
      datakey: 'endTime',
      sortkey: 'endTime',
      filterkey: 'endTime',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__END_TIME',
        defaultMessage: 'Termín realizácie do',
      }),
      width: 100,
      CellComponent: TableCells.DateTimeCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterLocalDateTimeCell,
      filterGroup: 2,
      filterOrder: 1,
      visible: false,
    },
    {
      datakey: 'duration',
      sortkey: 'duration',
      filterkey: 'duration',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__DURATION',
        defaultMessage: 'Trvanie',
      }),
      width: 100,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: DurationFilterComponent,
      filterGroup: 2,
      filterOrder: 2,
      valueMapper: durationColumnMapper,
    },
    {
      datakey: 'durationSap',
      sortkey: 'durationSap',
      filterkey: 'durationSap',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__DURATION_SAP',
        defaultMessage: 'Trvanie (SAP)',
      }),
      width: 100,
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: DurationFilterComponent,
      filterGroup: 2,
      filterOrder: 2,
      valueMapper: durationColumnMapper,
    },
    {
      datakey: 'earlyStart',
      sortkey: 'earlyStart',
      filterkey: 'earlyStart',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__EARLY_START',
        defaultMessage: 'Termín stretnutia od',
      }),
      width: 100,
      CellComponent: TableCells.DateTimeCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterLocalDateTimeCell,
      filterGroup: 2,
      filterOrder: 3,
      visible: false,
    },
    {
      datakey: 'dueDate',
      sortkey: 'dueDate',
      filterkey: 'dueDate',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__DUE_DATE',
        defaultMessage: 'Termín stretnutia do',
      }),
      width: 100,
      CellComponent: TableCells.DateTimeCell,
      sortable: true,
      filterable: true,
      filterGroup: 2,
      filterOrder: 4,
      visible: false,
    },
    {
      datakey: 'lateStart',
      sortkey: 'lateStart',
      filterkey: 'lateStart',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__LATE_START',
        defaultMessage: 'Začať plánovať najneskôr od',
      }),
      width: 100,
      CellComponent: TableCells.DateTimeCell,
      sortable: true,
      filterable: true,
      filterGroup: 2,
      filterOrder: 5,
      visible: false,
    },
    {
      datakey: 'travelPrevious',
      sortkey: 'travelPrevious',
      filterkey: 'travelPrevious',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__TRAVEL_PREVIOUS',
        defaultMessage: 'Prejazd k predchádzajúcemu',
      }),
      width: 100,
      CellComponent: TableCells.NumberCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 2,
      filterOrder: 6,
      visible: false,
    },
    {
      datakey: 'travelNext',
      sortkey: 'travelNext',
      filterkey: 'travelNext',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__TRAVEL_NEXT',
        defaultMessage: 'Prejazd k nasledujúcemu',
      }),
      width: 100,
      CellComponent: TableCells.NumberCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 2,
      filterOrder: 7,
      visible: false,
    },
    {
      datakey: 'previousOperation',
      sortkey: 'previousOperation.id',
      filterkey: 'previousOperation.id',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__PREVIOUS_OPERATION',
        defaultMessage: 'Predchádzajúci operácia',
      }),
      width: 100,
      CellComponent: function TextCell({ value, ...props }) {
        return <TableCells.TextCell value={value?.workOrderId} {...props} />;
      },
      sortable: true,
      filterable: true,
      FilterComponent:
        TableFilterCells.useFilterMultipleAutocompleteCellFactory(
          useWorkOrders,
          (wo) => wo.label ?? ''
        ),
      filterGroup: 2,
      filterOrder: 8,
      valueMapper: TableCells.dictionaryColumnMapper,
      visible: false,
    },
    {
      datakey: 'nextOperation',
      sortkey: 'nextOperation.id',
      filterkey: 'nextOperation.id',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__NEXT_OPERATION',
        defaultMessage: 'Nasledujúca operácia',
      }),
      width: 100,
      CellComponent: function TextCell({ value, ...props }) {
        return <TableCells.TextCell value={value?.workOrderId} {...props} />;
      },
      sortable: true,
      filterable: true,
      FilterComponent:
        TableFilterCells.useFilterMultipleAutocompleteCellFactory(
          useWorkOrders,
          (wo) => wo.label ?? ''
        ),
      filterGroup: 2,
      filterOrder: 9,
      valueMapper: TableCells.dictionaryColumnMapper,
      visible: false,
    },
    {
      datakey: 'dispatchDate',
      sortkey: 'dispatchDate',
      filterkey: 'dispatchDate',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__DISPATCH_DATE',
        defaultMessage: 'DispatchDate',
      }),
      width: 100,
      CellComponent: TableCells.DateTimeCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterLocalDateTimeCell,
      filterGroup: 2,
      filterOrder: 10,
    },

    ///////////////////////////////
    ...getAddressColumns(intl, 'address', 3, undefined, true),
    {
      datakey: 'latitude',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__LATITUDE',
        defaultMessage: 'Zemepisná šírka',
      }),
      width: 150,
      sortkey: 'latitude',
      filterkey: 'latitude',
      CellComponent: TableCells.NumberCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 3,
      filterOrder: 6,
      visible: false,
    },
    {
      datakey: 'longitude',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__LONGITUDE',
        defaultMessage: 'Zemepisná dĺžka',
      }),
      width: 150,
      sortkey: 'longitude',
      filterkey: 'longitude',
      CellComponent: TableCells.NumberCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 3,
      filterOrder: 7,
      visible: false,
    },

    {
      datakey: 'certifications',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CERTIFICATIONS',
        defaultMessage: 'Požadované kvalifikácie',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      sortkey: 'certifications.concatenated',
      filterable: true,
      filterkey: 'certifications.id',
      FilterComponent:
        TableFilterCells.useFilterMultipleAutocompleteCellFactory(
          useCertifications
        ),
      filterGroup: 4,
      filterOrder: 0,
      valueMapper: TableCells.dictionaryArrayColumnMapper,
      visible: false,
    },
    {
      datakey: 'mobileUnitSize',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__MOBILE_UNIT_SIZE',
        defaultMessage: 'Požadovaná veľkosť posádky',
      }),
      width: 150,
      sortkey: 'mobileUnitSize',
      filterkey: 'mobileUnitSize',
      CellComponent: TableCells.NumberCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 4,
      filterOrder: 1,
      visible: false,
    },
    {
      datakey: 'sameMobileUnit',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__SAME_MOBILE_UNIT',
        defaultMessage: 'Dokončiť rovnakou posádkou',
      }),
      width: 150,
      sortkey: 'sameMobileUnit',
      filterkey: 'sameMobileUnit',
      CellComponent: TableCells.BooleanCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterBooleanCell,
      filterGroup: 4,
      filterOrder: 2,
      visible: false,
    },
    {
      datakey: 'numberOfAffectedCustomers',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__NUMBER_OF_AFFECTED_CUSTOMERS',
        defaultMessage: 'Počet postihnutých OM',
      }),
      width: 150,
      sortkey: 'numberOfAffectedCustomers',
      filterkey: 'numberOfAffectedCustomers',
      CellComponent: TableCells.NumberCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterNumberCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 5,
      filterOrder: 0,
      visible: false,
    },
    {
      datakey: 'shortDescription',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__SHORT_DESCRIPTION',
        defaultMessage: 'Krátky popis',
      }),
      width: 150,
      sortkey: 'shortDescription',
      filterkey: 'shortDescription',
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 5,
      filterOrder: 1,
      visible: false,
    },
    {
      datakey: 'expectedMaterial',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__EXPECTED_MATERIAL',
        defaultMessage: 'Očakávaný materiál',
      }),
      width: 150,
      sortkey: 'expectedMaterial',
      filterkey: 'expectedMaterial',
      CellComponent: TableCells.BooleanCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterBooleanCell,
      filterGroup: 5,
      filterOrder: 2,
      visible: false,
    },
    // TODO:
    // remove after named filters are reworked as custom name filters
    // we will get it from getAddressColumns
    // https://jira.zsdis.sk/browse/FSMS-920
    {
      datakey: `address.customerName`,
      sortkey: `address.customerName`,
      filterkey: `address.customerName`,
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CUSTOMER_NAME',
        defaultMessage: 'Meno zákazníka',
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      filterable: true,
      sortable: true,

      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup: 6,
      filterOrder: 0,
      visible: false,
    },
    ...getAddressColumns(intl, 'customerAddress', 7, 'Adresa zákazníka', false),
    ...getAddressColumns(
      intl,
      'mailingAddress',
      8,
      'Korešpondenčná adresa',
      false
    ),
    {
      datakey: 'imsCategory',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__IMS_CATEGORY',
        defaultMessage: 'Kategória IMS',
      }),
      width: 150,
      sortkey: 'imsCategory',
      filterkey: 'imsCategory',
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: FieldFilter.FilterTextCell,
      filterOperation: ApiFilterOperation.EQ,
      filterGroup: 9,
      filterOrder: 0,
      visible: false,
    },
    {
      datakey: '_technicalObjects-name',

      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__TECHNICAL_OBJECT_NAME',
        defaultMessage: 'Názov technického objektu',
      }),
      width: 150,

      filterable: true,
      sortable: false,
      sortkey: 'technicalObjects.technicalObjects.name',
      CellComponent: getTechnicalObjectCell(
        'technicalObject.name',
        (length) => `a ďalších ${length - 1} názvov TO`
      ),
      FilterComponent: TableFilterCells.FilterTextCell,
      filterkey: 'technicalObjects.technicalObject.name',
      filterPath: 'technicalObjects',

      filterOperation: ApiFilterOperation.CONTAINS,
      filterGroup: 10,
      filterOrder: 0,
      visible: true,
    },
    {
      datakey: '_technicalObjects-type',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__TECHNICAL_OBJECT_TYPE',
        defaultMessage: 'Typ technického objektu',
      }),
      width: 150,
      sortkey: 'technicalObjects.type.id',
      filterkey: 'technicalObjects.technicalObject.type.id',
      filterPath: 'technicalObjects',
      CellComponent: getTechnicalObjectCell(
        'technicalObject.type.name',
        (length) => `a ďalších ${length - 1} typov TO`
      ),
      sortable: false,
      filterable: true,
      FilterComponent:
        TableFilterCells.useFilterMultipleAutocompleteCellFactory(
          useTechnicalObjectTypes
        ),
      filterOperation: ApiFilterOperation.NESTED,
      filterGroup: 10,
      filterOrder: 1,
      visible: true,
    },
    {
      datakey: 'road',
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__ROAD',
        defaultMessage: 'Cesta',
      }),
      width: 150,
      filterkey: 'road',
      sortkey: 'road',
      CellComponent: TableCells.TextCell,
      sortable: true,
      filterable: true,
      FilterComponent: TableFilterCells.FilterTextCell,
      filterOperation: ApiFilterOperation.CONTAINS,
      filterGroup: 10,
      filterOrder: 2,
      visible: true,
    },
  ];
}

const getAddressColumns = (
  intl: ReturnType<typeof useIntl>,
  addressKey: string,
  filterGroup: number,
  labelPrefix?: string,
  // TODO:
  // remove after named filters are reworked as custom name filters
  // https://jira.zsdis.sk/browse/FSMS-920
  omitCustomerName?: boolean
) => {
  return [
    {
      datakey: `${addressKey}.city`,
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CITY',
        defaultMessage: `${labelPrefix ? labelPrefix + ' - ' : ''}Mesto`,
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      sortkey: `${addressKey}.city`,
      filterable: true,
      filterkey: `${addressKey}.city`,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup,
      filterOrder: 0,
      visible: false,
    },
    {
      datakey: `${addressKey}.street`,
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__STREET',
        defaultMessage: `${labelPrefix ? labelPrefix + ' - ' : ''}Ulica`,
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      sortkey: `${addressKey}.street`,
      filterable: true,
      filterkey: `${addressKey}.street`,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup,
      filterOrder: 1,
      visible: false,
    },
    {
      datakey: `${addressKey}.houseNumber`,
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__HOUSE_NUMBER',
        defaultMessage: `${
          labelPrefix ? labelPrefix + ' - ' : ''
        }Číslo popisné`,
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      sortkey: `${addressKey}.houseNumber`,
      filterable: true,
      filterkey: `${addressKey}.houseNumber`,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup,
      filterOrder: 2,
      visible: false,
    },
    {
      datakey: `${addressKey}.postCode`,
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__POST_CODE',
        defaultMessage: `${labelPrefix ? labelPrefix + ' - ' : ''}PSČ`,
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      sortkey: `${addressKey}.postCode`,
      filterable: true,
      filterkey: `${addressKey}.postCode`,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup,
      filterOrder: 3,
      visible: false,
    },
    {
      datakey: `${addressKey}.building`,
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__BUILDING',
        defaultMessage: `${labelPrefix ? labelPrefix + ' - ' : ''}Budova`,
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      sortkey: `${addressKey}.building`,
      filterable: true,
      filterkey: `${addressKey}.building`,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup,
      filterOrder: 4,
      visible: false,
    },
    {
      datakey: `${addressKey}.additionalInfo`,
      name: intl.formatMessage({
        id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__ADDITIONAL_INFO',
        defaultMessage: `${labelPrefix ? labelPrefix + ' - ' : ''}Dodatok`,
      }),
      width: 150,
      CellComponent: TableCells.TextCell,
      sortable: true,
      sortkey: `${addressKey}.additionalInfo`,
      filterable: true,
      filterkey: `${addressKey}.additionalInfo`,
      FilterComponent: FieldFilter.FilterTextCell,
      filterGroup,
      filterOrder: 5,
      visible: false,
    },
    ...(omitCustomerName
      ? []
      : [
          {
            datakey: `${addressKey}.customerName`,
            sortkey: `${addressKey}.customerName`,
            filterkey: `${addressKey}.customerName`,
            name: intl.formatMessage({
              id: 'ZSD__WORK_ORDERS__TABLE__COLUMN__CUSTOMER_NAME',
              defaultMessage: `${
                labelPrefix ? labelPrefix + ' - ' : ''
              }Meno zákazníka`,
            }),
            width: 150,
            CellComponent: TableCells.TextCell,
            filterable: true,
            sortable: true,

            FilterComponent: FieldFilter.FilterTextCell,
            filterGroup,
            filterOrder: 6,
            visible: false,
          },
        ]),
  ];
};

// 11.11.2025
// This list is used to import old saved named settings for table under the calendar.
// EAS named filters doesnt support filter state labeling by datakey
// so it is needed to know last state of filters before migration
// to custom named filters module

export const COLUMNS_KEYS_ORDER_SNAPHOT = [
  'workOrderId',
  'operationNumber',
  'received',
  'priority',
  'currentState',
  'workOrderType',
  'department.name',
  'department.team.name',
  'department.team.section.name',
  'mobileUnit',
  'user',
  'locked',
  'fixed',
  'indivisible',
  'customerAgreed',
  'maintenanceFailure',
  'inDanger',
  'risk',
  'technicalPlaceSap',
  'standardTextKey',
  'customerPhoneNumber',
  'callId',
  'equipmentId',
  'equipmentText',
  'customerName',
  'consumptionPlaceNumber',
  'hdoCode',
  'mainCircuitBreaker',
  'oldTariffType',
  'newTariffType',
  'oldTariffication',
  'newTarification',
  'consumptionPointTypeDescription',
  'meterSerialId',
  'elmMaterialType',
  'integratedHdoCode',
  'billingArea',
  'vgk1',
  'vgk2',
  'vgk3',
  'customerVulnerable',
  'customerVip',
  'ownerTo',
  'numberOfTaskFailure',
  'startTime',
  'endTime',
  'duration',
  'durationSap',
  'earlyStart',
  'dueDate',
  'lateStart',
  'travelPrevious',
  'travelNext',
  'previousOperation',
  'nextOperation',
  'dispatchDate',
  'address.city',
  'address.street',
  'address.houseNumber',
  'address.postCode',
  'address.building',
  'address.additionalInfo',
  'latitude',
  'longitude',
  'certifications',
  'mobileUnitSize',
  'sameMobileUnit',
  'numberOfAffectedCustomers',
  'shortDescription',
  'expectedMaterial',
  'address.customerName',
  'customerAddress.city',
  'customerAddress.street',
  'customerAddress.houseNumber',
  'customerAddress.postCode',
  'customerAddress.building',
  'customerAddress.additionalInfo',
  'customerAddress.customerName',
  'mailingAddress.city',
  'mailingAddress.street',
  'mailingAddress.houseNumber',
  'mailingAddress.postCode',
  'mailingAddress.building',
  'mailingAddress.additionalInfo',
  'mailingAddress.customerName',
  'imsCategory',
];
